var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Modal, useMediaQuery, FormLabel, Chip, MenuItem, Select, ToggleButton, Tooltip, CircularProgress, IconButton, } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import ComboboxBasic from '@components/ComboboxBasic';
import CustomDatePicker from '@components/CustomDatePicker';
import ErrorMessage from '@components/ErrorMessage';
import GiftCardInput from './GiftCardInput';
import BookingBreakdown from '../BookingBreakdown';
import IntegratedCheckout from '../../CheckoutPage/CheckoutPage';
import { V2_API_URL } from '@settings';
import { useTranslation } from 'react-i18next';
import { Add, Remove } from '@mui/icons-material';
import ShieldIcon from '@mui/icons-material/Shield';
import LeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import * as FaIcons from 'react-icons/fa';
import { calcHourlyBookingDetails, calcDailyBookingDetails, } from './calc';
dayjs.extend(utc);
dayjs.extend(timezone);
/** Finds the appropriate pricing season for a given date. */
function findSeasonForDate(date, seasons) {
    return seasons === null || seasons === void 0 ? void 0 : seasons.find((season) => {
        const start = dayjs(season.startDate, 'YYYY-MM-DD');
        const end = dayjs(season.endDate, 'YYYY-MM-DD');
        return date.isBetween(start, end, 'day', '[]');
    });
}
/** Validates that each day in [startDay..endDay] is not 'hourlyOnly'/'unavailable'/'waitlisted'. */
function validateMultiDayRange(startDay, endDay, getStatus) {
    let day = startDay.clone();
    while (day.isSameOrBefore(endDay, 'day')) {
        const status = getStatus(day);
        if (status === 'hourlyOnly' || status === 'unavailable' || status === 'waitlisted') {
            return false;
        }
        day = day.add(1, 'day');
    }
    return true;
}
/** STAFF HELPERS **/
// Returns true only if every required role can be assigned a unique staff member.
function canAssignStaff(staffForDay, staffAssignments) {
    if (!staffAssignments.length)
        return true; // if no staffing is required, trivially true
    const requirements = staffAssignments.map((assign) => assign.skill);
    function backtrack(index, used) {
        if (index === requirements.length)
            return true;
        const skill = requirements[index];
        const availableStaffIds = (staffForDay[skill] || []).map((s) => s.id);
        for (const staffId of availableStaffIds) {
            if (!used.has(staffId)) {
                used.add(staffId);
                if (backtrack(index + 1, used))
                    return true;
                used.delete(staffId);
            }
        }
        return false;
    }
    return backtrack(0, new Set());
}
// Try to assign unique staff for each required role.
// Returns a mapping of skill to an array of assigned staff IDs. (Empty if unique assignment fails.)
function autoAssignStaff(results, staffAssignments) {
    const availableMap = {};
    results.forEach((r) => {
        availableMap[r.skill] = r.staff;
    });
    const requirements = staffAssignments.map((assign) => assign.skill);
    const matching = {};
    function backtrack(index, used) {
        if (index === requirements.length)
            return true;
        const skill = requirements[index];
        const available = (availableMap[skill] || []).map((s) => s.id);
        for (const staffId of available) {
            if (!used.has(staffId)) {
                used.add(staffId);
                matching[`${skill}-${index}`] = staffId;
                if (backtrack(index + 1, used))
                    return true;
                used.delete(staffId);
            }
        }
        return false;
    }
    const selectedStaffMap = {};
    if (backtrack(0, new Set())) {
        requirements.forEach((skill, index) => {
            const key = `${skill}-${index}`;
            if (!selectedStaffMap[skill]) {
                selectedStaffMap[skill] = [];
            }
            selectedStaffMap[skill].push(matching[key]);
        });
        return selectedStaffMap;
    }
    return {};
}
export default function DateRangeSelector({ selectedDateRange, handleDateRangeChange, isValidRange, startTime, handleTimeChange, useHourlyPricing, schedule, maxHoursPerDay, giftCardCode, setGiftCardCode, handleApplyGiftCard, isApplyingPromo, giftCardError, boatRefresh, boatId, discountType, discount, giftCardDiscount, loading, refAffiliate, }) {
    var _a, _b, _c, _d, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery('(max-width:1000px)');
    // Set default timezone
    dayjs.tz.setDefault((_b = (_a = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _a === void 0 ? void 0 : _a.details) === null || _b === void 0 ? void 0 : _b.timezone);
    // STAFF DATA: only load if boat has a staffing requirement
    const [staffByDay, setStaffByDay] = useState({});
    const [rangeLoading, setRangeLoading] = useState(false);
    // Local UI states
    const [duration, setDuration] = useState(1);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [dayRangeError, setDayRangeError] = useState('');
    // Waitlist states
    const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
    const [waitlistForm, setWaitlistForm] = useState({ firstName: '', lastName: '', email: '' });
    const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
    const [waitlistSuccess, setWaitlistSuccess] = useState(false);
    const [waitlistError, setWaitlistError] = useState(null);
    const [selectedWaitlistDate, setSelectedWaitlistDate] = useState(null);
    // Checkout
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [checkoutData, setCheckoutData] = useState(null);
    // Add-ons
    const [selectedGlobalAddons, setSelectedGlobalAddons] = useState([]);
    const [selectedBoatAddons, setSelectedBoatAddons] = useState([]);
    // Staffing assignments from boat; only poll if non-empty
    const staffAssignments = (boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.StaffAssignments) || [];
    const [availableStaffMap, setAvailableStaffMap] = useState({});
    const [selectedStaffMap, setSelectedStaffMap] = useState({});
    const [staffLoading, setStaffLoading] = useState(false);
    const [debounceTimer, setDebounceTimer] = useState(null);
    const cancelSourceRef = useRef(null);
    // Cache for availability statuses
    const availabilityCache = useRef({});
    // Deconstruct boat-related values
    const capacity = (_c = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Capacity) !== null && _c !== void 0 ? _c : 1;
    const defaultLayover = (_d = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.DefaultLayover) !== null && _d !== void 0 ? _d : 0;
    const canWaitlist = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.AllowsWaitlist;
    const seasons = ((_f = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Pricing) === null || _f === void 0 ? void 0 : _f.Seasons) || [];
    const [startDate, endDateVal] = selectedDateRange;
    const depositPercentage = (boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.DepositPercentage) || 0;
    // Payment/taxes
    const platformFee = ((_h = (_g = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Customer) === null || _g === void 0 ? void 0 : _g.permissions) === null || _h === void 0 ? void 0 : _h.transactionFee) || 0;
    const interchangeFee = ((_k = (_j = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Customer) === null || _j === void 0 ? void 0 : _j.permissions) === null || _k === void 0 ? void 0 : _k.interchangeFee) || 0.024;
    const fee = platformFee + interchangeFee;
    const taxes = ((_m = (_l = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _l === void 0 ? void 0 : _l.details) === null || _m === void 0 ? void 0 : _m.taxRates) || [];
    const totalTaxRate = taxes.reduce((acc, item) => acc + item.rate, 0);
    const TAX_AND_FEE_RATE = fee + totalTaxRate / 100;
    // Season details for pricing
    const currentSeason = startDate ? findSeasonForDate(startDate, seasons) : null;
    const minDays = (currentSeason === null || currentSeason === void 0 ? void 0 : currentSeason.minimumDays) || 1;
    const maxDays = (currentSeason === null || currentSeason === void 0 ? void 0 : currentSeason.maximumDays) || 30;
    const dayOfWeek = startDate ? startDate.format('dddd') : '';
    const hourData = ((_o = currentSeason === null || currentSeason === void 0 ? void 0 : currentSeason.hourlyPrices) === null || _o === void 0 ? void 0 : _o[dayOfWeek]) || null;
    const dayMinHours = (hourData === null || hourData === void 0 ? void 0 : hourData.minimumHours) || 1;
    const dayMaxHours = (hourData === null || hourData === void 0 ? void 0 : hourData.maximumHours) || maxHoursPerDay;
    const allowedHours = Array.from({ length: dayMaxHours - dayMinHours + 1 }, (_, i) => i + dayMinHours);
    // Same-day booking?
    const sameDaySelected = useMemo(() => startDate && endDateVal && startDate.isSame(endDateVal, 'day'), [startDate, endDateVal]);
    // Final end time (for hourly bookings)
    const finalEndTime = useMemo(() => {
        if (sameDaySelected && startTime) {
            return startTime.add(duration, 'hour');
        }
        return null;
    }, [sameDaySelected, startTime, duration]);
    // Booking details state
    const [bookingDetailsState, setBookingDetails] = useState({
        subtotal: 0,
        daysBooked: 0,
        hoursBooked: 0,
        averagePrice: 0,
        fees: 0,
        discount: 0,
        discountValue: 0,
        giftCardAmount: 0,
        totalPrice: 0,
        addonsTotal: 0,
        tax: taxes,
        depositPercentage,
        depositDueNow: 0,
        remainingBalance: 0,
    });
    // Ensure minimum duration is used
    useEffect(() => {
        if (allowedHours.length > 0 && duration < allowedHours[0]) {
            setDuration(allowedHours[0]);
        }
    }, [allowedHours, duration]);
    /** (1) On mount: load staff range if staffing is required */
    useEffect(() => {
        if (!staffAssignments.length) {
            setStaffByDay({});
            return;
        }
        function loadStaffRange() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    setRangeLoading(true);
                    const rangeStart = dayjs().startOf('day');
                    const rangeEnd = dayjs().add(30, 'day').endOf('day');
                    const url = `${V2_API_URL}/public/available-staff-range`;
                    const locationId = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.CustomerID;
                    const resp = yield axios.get(url, {
                        params: {
                            locationId,
                            start: rangeStart.format('YYYY-MM-DD'),
                            end: rangeEnd.format('YYYY-MM-DD'),
                        },
                    });
                    if (resp.data && resp.data.staffByDay) {
                        setStaffByDay(resp.data.staffByDay);
                    }
                    else {
                        setStaffByDay({});
                    }
                }
                catch (err) {
                    console.error('Failed to load staff range', err);
                    setStaffByDay({});
                }
                finally {
                    setRangeLoading(false);
                }
            });
        }
        loadStaffRange();
    }, [boatRefresh, staffAssignments]);
    /** (2) Debounced fetch for available staff (only if staffing is required) */
    useEffect(() => {
        if (!staffAssignments.length || !startDate || !endDateVal) {
            setAvailableStaffMap({});
            setSelectedStaffMap({});
            return;
        }
        if (debounceTimer)
            clearTimeout(debounceTimer);
        if (cancelSourceRef.current) {
            cancelSourceRef.current.cancel('Canceled previous staff fetch');
        }
        const startISO = startTime
            ? startTime.toISOString()
            : dayjs(startDate).hour(9).minute(0).toISOString();
        const endISO = finalEndTime
            ? finalEndTime.toISOString()
            : dayjs(endDateVal).hour(17).minute(0).toISOString();
        const newTimer = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            try {
                setStaffLoading(true);
                const source = axios.CancelToken.source();
                cancelSourceRef.current = source;
                const locationId = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.CustomerID;
                const skillFetches = staffAssignments.map((assign) => {
                    const skill = assign.skill;
                    return axios
                        .get(`${V2_API_URL}/public/available-staff`, {
                        params: { locationId, skill, startTime: startISO, endTime: endISO },
                        cancelToken: source.token,
                    })
                        .then((resp) => ({
                        skill,
                        staff: resp.data.staff || [],
                    }));
                });
                const results = yield Promise.all(skillFetches);
                const newMap = {};
                results.forEach((r) => {
                    newMap[r.skill] = r.staff;
                });
                setAvailableStaffMap(newMap);
                // Auto-assign unique staff if possible
                const autoAssigned = autoAssignStaff(results, staffAssignments);
                setSelectedStaffMap(autoAssigned);
            }
            catch (err) {
                if (!axios.isCancel(err)) {
                    console.error('Error fetching staff:', err);
                    const emptyMap = {};
                    staffAssignments.forEach((assign) => {
                        emptyMap[assign.skill] = [];
                    });
                    setAvailableStaffMap(emptyMap);
                    setSelectedStaffMap({});
                }
            }
            finally {
                setStaffLoading(false);
                cancelSourceRef.current = null;
            }
        }), 500);
        setDebounceTimer(newTimer);
        return () => clearTimeout(newTimer);
    }, [staffAssignments, startDate, endDateVal, startTime, finalEndTime, boatRefresh]);
    /** (3) Generate Time Slots based on the day’s schedule and staff availability */
    const generateTimeSlots = useCallback((date, bookingDuration) => {
        var _a, _b;
        if (!date)
            return [];
        const dateKey = date.format('YYYY-MM-DD');
        const staffForDay = staffByDay[dateKey] || {};
        // If required staffing cannot be uniquely assigned, return no slots (i.e. hide the chips)
        if (!canAssignStaff(staffForDay, staffAssignments)) {
            return [];
        }
        const dayOfWeekSlot = date.format('dddd');
        const daySchedule = schedule === null || schedule === void 0 ? void 0 : schedule[dayOfWeekSlot];
        if (!daySchedule || daySchedule.isClosed || !daySchedule.open || !daySchedule.close) {
            return [];
        }
        const [openHour, openMinute] = daySchedule.open.split(':').map(Number);
        const [closeHour, closeMinute] = daySchedule.close.split(':').map(Number);
        const scheduleOpen = date.set('hour', openHour).set('minute', openMinute);
        const scheduleClose = date.set('hour', closeHour).set('minute', closeMinute);
        const SLOT_STEP_MINUTES = ((_b = (_a = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _a === void 0 ? void 0 : _a.details) === null || _b === void 0 ? void 0 : _b.slotSteps) || 30;
        const now = dayjs();
        const slots = [];
        for (let slotStart = scheduleOpen; slotStart.add(bookingDuration, 'hour').isSameOrBefore(scheduleClose); slotStart = slotStart.add(SLOT_STEP_MINUTES, 'minute')) {
            const slotEnd = slotStart.add(bookingDuration, 'hour');
            const overlappingCount = ((boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Bookings) || []).reduce((count, bk) => {
                if (!bk.startDate || !bk.endDate)
                    return count;
                const bookingStart = dayjs(bk.startDate).subtract(defaultLayover, 'minute');
                const bookingEnd = dayjs(bk.endDate).add(defaultLayover, 'minute');
                return slotStart.isBefore(bookingEnd) && slotEnd.isAfter(bookingStart) ? count + 1 : count;
            }, 0);
            const isPast = date.isSame(now, 'day') && slotStart.isBefore(now);
            const disabled = overlappingCount >= capacity || isPast;
            slots.push({
                label: `${slotStart.format('h:mm A')} - ${slotEnd.format('h:mm A')}`,
                start: slotStart,
                end: slotEnd,
                disabled,
            });
        }
        return slots;
    }, [staffByDay, staffAssignments, schedule, boatRefresh, capacity, defaultLayover]);
    /** (4) Determine Availability Status for a day */
    const getAvailabilityStatus = useCallback((day) => {
        var _a, _b;
        const key = day.format('YYYY-MM-DD');
        if (availabilityCache.current[key])
            return availabilityCache.current[key];
        if (day.isBefore(dayjs(), 'day')) {
            availabilityCache.current[key] = 'unavailable';
            return 'unavailable';
        }
        const dayOfWeekSlot = day.format('dddd');
        const daySchedule = schedule === null || schedule === void 0 ? void 0 : schedule[dayOfWeekSlot];
        if (!daySchedule || daySchedule.isClosed || !daySchedule.open || !daySchedule.close) {
            availabilityCache.current[key] = 'unavailable';
            return 'unavailable';
        }
        const seasonForDay = findSeasonForDate(day, seasons);
        if (!seasonForDay) {
            availabilityCache.current[key] = 'unavailable';
            return 'unavailable';
        }
        const isOnWaitlist = ((_a = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.WaitlistedDays) === null || _a === void 0 ? void 0 : _a.some((w) => day.isSame(dayjs(w), 'day'))) || false;
        if (isOnWaitlist) {
            availabilityCache.current[key] = 'waitlisted';
            return 'waitlisted';
        }
        let canDay = seasonForDay.useDayPricing;
        let canHour = seasonForDay.useHourlyPricing;
        if (canDay) {
            const dayStart = day.startOf('day');
            const dayEnd = day.endOf('day');
            const overlappingBookingsCount = ((boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Bookings) || []).filter((bk) => {
                if (!bk.startDate || !bk.endDate)
                    return false;
                const bookingStart = dayjs(bk.startDate);
                const bookingEnd = dayjs(bk.endDate);
                return bookingStart.isBefore(dayEnd) && bookingEnd.isAfter(dayStart);
            }).length;
            if (overlappingBookingsCount >= capacity) {
                canDay = false;
            }
        }
        let hasHourlySlots = false;
        if (canHour) {
            const hourData2 = (_b = seasonForDay.hourlyPrices) === null || _b === void 0 ? void 0 : _b[dayOfWeekSlot];
            const minH = (hourData2 === null || hourData2 === void 0 ? void 0 : hourData2.minimumHours) || 1;
            const maxH = (hourData2 === null || hourData2 === void 0 ? void 0 : hourData2.maximumHours) || maxHoursPerDay;
            const durations = Array.from({ length: maxH - minH + 1 }, (_, i) => minH + i);
            const dateKey = day.format('YYYY-MM-DD');
            const staffForDay = staffByDay[dateKey] || {};
            for (const dur of durations) {
                if (!canAssignStaff(staffForDay, staffAssignments))
                    continue;
                const slots = generateTimeSlots(day, dur);
                if (slots.some((s) => !s.disabled)) {
                    hasHourlySlots = true;
                    break;
                }
            }
        }
        let result;
        if (canDay)
            result = 'dailyAndHourly';
        else if (!canDay && hasHourlySlots)
            result = 'hourlyOnly';
        else
            result = canWaitlist ? 'waitlisted' : 'unavailable';
        availabilityCache.current[key] = result;
        return result;
    }, [schedule, seasons, boatRefresh, capacity, canWaitlist, maxHoursPerDay, staffByDay, staffAssignments, generateTimeSlots]);
    const shouldDisableDate = useCallback((day) => getAvailabilityStatus(day) === 'unavailable', [getAvailabilityStatus]);
    // Memoize time slots for the selected date/duration
    const timeSlots = useMemo(() => generateTimeSlots(startDate, duration), [startDate, duration, generateTimeSlots]);
    const isHourlyOnly = useMemo(() => startDate && getAvailabilityStatus(startDate) === 'hourlyOnly', [startDate, getAvailabilityStatus]);
    // Force end date = start date if booking is hourly-only
    useEffect(() => {
        if (startDate && isHourlyOnly && (!endDateVal || !startDate.isSame(endDateVal, 'day'))) {
            handleDateRangeChange([startDate, startDate]);
        }
    }, [startDate, endDateVal, isHourlyOnly, handleDateRangeChange]);
    // If endDate < startDate, adjust it
    useEffect(() => {
        if (startDate && endDateVal && endDateVal.isBefore(startDate, 'day')) {
            handleDateRangeChange([startDate, startDate]);
        }
    }, [startDate, endDateVal, handleDateRangeChange]);
    // (5) Update pricing based on selected dates/duration
    useEffect(() => {
        setDayRangeError('');
        if (!startDate || !endDateVal)
            return;
        const dayDiff = endDateVal.diff(startDate, 'day') + 1;
        if (dayDiff > 1 && !validateMultiDayRange(startDate, endDateVal, getAvailabilityStatus)) {
            setDayRangeError(t('dateRangeSelector.errorMultiDay'));
            return;
        }
        const bookingHourly = isHourlyOnly || (useHourlyPricing && startDate.isSame(endDateVal, 'day'));
        if (bookingHourly) {
            const { bookingDetails, error } = calcHourlyBookingDetails({
                duration,
                startDate,
                currentSeason,
                dayOfWeek,
                discountType,
                giftCardDiscount,
                discount,
                selectedGlobalAddons,
                selectedBoatAddons,
                boatRefresh,
                TAX_AND_FEE_RATE,
                taxes,
                depositPercentage,
            });
            error ? setDayRangeError(error) : setBookingDetails(bookingDetails);
        }
        else {
            const { bookingDetails, error } = calcDailyBookingDetails({
                startDate,
                endDate: endDateVal,
                currentSeason,
                minDays,
                maxDays,
                discountType,
                giftCardDiscount,
                discount,
                selectedGlobalAddons,
                selectedBoatAddons,
                boatRefresh,
                totalTaxRate,
                fee,
                depositPercentage,
            });
            error ? setDayRangeError(error) : setBookingDetails(bookingDetails);
        }
    }, [
        startDate,
        endDateVal,
        duration,
        useHourlyPricing,
        isHourlyOnly,
        dayOfWeek,
        discountType,
        discount,
        giftCardDiscount,
        selectedGlobalAddons,
        selectedBoatAddons,
        startTime,
        getAvailabilityStatus,
        boatRefresh,
        minDays,
        maxDays,
        fee,
        totalTaxRate,
        TAX_AND_FEE_RATE,
        depositPercentage,
        handleDateRangeChange,
        t,
    ]);
    // (6) Global Add-on Handlers
    const handleGlobalAddonChange = (addonName, addonPercentage, minimum) => {
        setSelectedGlobalAddons((prev) => {
            const found = prev.find((x) => x.name === addonName);
            return found ? prev.filter((x) => x.name !== addonName) : [...prev, { name: addonName, percentage: addonPercentage, minimum }];
        });
    };
    // (7) Boat Add-on Handlers
    const handleIncrementBoatAddon = (addon) => {
        setSelectedBoatAddons((prev) => {
            const existing = prev.find((x) => x.name === addon.name);
            return existing
                ? prev.map((x) => (x.name === addon.name ? Object.assign(Object.assign({}, x), { quantity: x.quantity + 1 }) : x))
                : [...prev, { name: addon.name, quantity: 1, required: addon.required, description: addon.description }];
        });
    };
    const handleDecrementBoatAddon = (addon) => {
        setSelectedBoatAddons((prev) => {
            const existing = prev.find((x) => x.name === addon.name);
            if (!existing)
                return prev;
            const newQty = existing.quantity - 1;
            if (newQty < (addon.required ? 1 : 0))
                return prev;
            else if (newQty === 0)
                return prev.filter((x) => x.name !== addon.name);
            else
                return prev.map((x) => (x.name === addon.name ? Object.assign(Object.assign({}, x), { quantity: newQty }) : x));
        });
    };
    // (8) Book Now: prepare final booking data
    const handleBookNow = () => {
        var _a, _b, _c, _d;
        if (dayRangeError)
            return;
        const bookingHourly = isHourlyOnly || (useHourlyPricing && sameDaySelected);
        if (bookingHourly && !selectedSlot) {
            setDayRangeError(t('dateRangeSelector.errorSelectTimeSlot'));
            return;
        }
        if (bookingHourly) {
            const slotOk = selectedSlot &&
                !selectedSlot.isBefore(dayjs()) &&
                !timeSlots.some((slot) => slot.disabled && slot.start.isSame(selectedSlot));
            if (!slotOk) {
                if (canWaitlist)
                    setIsWaitlistOpen(true);
                else
                    setDayRangeError(t('dateRangeSelector.errorSlotUnavailable'));
                return;
            }
        }
        const finalStaffAssignments = staffAssignments.map((assign) => {
            const skill = assign.skill;
            const staffIds = selectedStaffMap[skill] || [];
            const staffProfiles = staffIds.map((id) => {
                var _a;
                const staff = (_a = availableStaffMap[skill]) === null || _a === void 0 ? void 0 : _a.find((s) => s.id === id);
                return staff ? staff.profileName : '';
            });
            return { skill, staffIds, staffProfiles };
        });
        const computedTaxBreakdown = bookingDetailsState.subtotal
            ? taxes
                .filter((taxObj) => taxObj.boat)
                .map((taxObj) => ({
                name: taxObj.name,
                rate: taxObj.rate,
                amount: bookingDetailsState.subtotal * (taxObj.rate / 100),
            }))
            : [];
        const finalData = {
            handleGoBack: () => setCheckoutOpen(false),
            selectedBoatAddons: {
                selectedBoatAddonsTotal: bookingDetailsState.addonsTotal,
                selectedBoatAddons,
            },
            rentalPricing: {
                averagePrice: bookingDetailsState.averagePrice,
                fees: bookingDetailsState.fees,
                subtotal: bookingDetailsState.subtotal,
                totalPrice: bookingDetailsState.totalPrice,
                tax: taxes,
                currency: ((_b = (_a = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _a === void 0 ? void 0 : _a.details) === null || _b === void 0 ? void 0 : _b.currency) || 'USD',
            },
            rentalDetails: {
                boatId,
                boatName: boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.BoatName,
                locationId: boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.LocationID,
                customerId: boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.CustomerID,
                feePercentage: fee,
                interchangeFee,
                platformFee,
                isHourlyRental: bookingDetailsState.hoursBooked > 0,
                hoursBooked: bookingDetailsState.hoursBooked,
                daysBooked: bookingDetailsState.daysBooked,
                timeZone: ((_d = (_c = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _c === void 0 ? void 0 : _c.details) === null || _d === void 0 ? void 0 : _d.timezone) || 'America/New_York',
                startDate: startTime ? startTime.format('YYYY-MM-DD HH:mm') : startDate === null || startDate === void 0 ? void 0 : startDate.format('YYYY-MM-DD 09:00'),
                endDate: finalEndTime ? finalEndTime.format('YYYY-MM-DD HH:mm') : endDateVal === null || endDateVal === void 0 ? void 0 : endDateVal.format('YYYY-MM-DD 17:00'),
            },
            discountDetails: {
                discountType,
                discount: bookingDetailsState.discount,
                giftCardAmount: giftCardDiscount,
                giftCardDiscount: bookingDetailsState.giftCardDiscount,
                giftCardCode: giftCardCode,
            },
            globalAddons: {
                selectedGlobalAddons,
            },
            depositInformation: {
                depositPercentage: bookingDetailsState.depositPercentage,
                depositDueNow: bookingDetailsState.depositDueNow,
                remainingBalance: bookingDetailsState.remainingBalance,
            },
            affiliate: {
                affiliateReference: refAffiliate,
            },
            taxBreakdown: computedTaxBreakdown,
            extendedData: { boatRefresh },
            staffAssignments: finalStaffAssignments,
        };
        setCheckoutData(finalData);
        setCheckoutOpen(true);
    };
    // (9) Waitlist handlers
    const handleWaitlistDayClick = (day) => {
        if (canWaitlist) {
            setSelectedWaitlistDate(day);
            setIsWaitlistOpen(true);
        }
        else {
            setDayRangeError(t('dateRangeSelector.errorWaitlistUnavailable'));
        }
    };
    const handleWaitlistInputChange = (e) => {
        const { name, value } = e.target;
        setWaitlistForm((prev) => (Object.assign(Object.assign({}, prev), { [name]: value })));
    };
    const handleWaitlistSubmit = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const { firstName, lastName, email } = waitlistForm;
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            setWaitlistError(t('dateRangeSelector.errorAllFieldsRequired'));
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setWaitlistError(t('dateRangeSelector.errorInvalidEmail'));
            return;
        }
        setWaitlistSubmitting(true);
        setWaitlistError(null);
        try {
            const waitlistData = {
                firstName,
                lastName,
                email,
                date: (selectedWaitlistDate === null || selectedWaitlistDate === void 0 ? void 0 : selectedWaitlistDate.format('YYYY-MM-DD')) || '',
                boatId,
                refAffiliate,
            };
            const response = yield axios.post(`${V2_API_URL}/public/waitlist`, waitlistData);
            if (response.status === 200 || response.status === 201) {
                setWaitlistSuccess(true);
                setTimeout(() => {
                    setIsWaitlistOpen(false);
                    setWaitlistSuccess(false);
                    setWaitlistForm({ firstName: '', lastName: '', email: '' });
                    setSelectedWaitlistDate(null);
                }, 3000);
            }
            else {
                setWaitlistError(t('dateRangeSelector.errorWaitlistSubmit'));
            }
        }
        catch (error) {
            console.error(error);
            setWaitlistError(((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || t('dateRangeSelector.errorWaitlistSubmit'));
        }
        finally {
            setWaitlistSubmitting(false);
        }
    });
    // (10) Render tax details summary
    const taxDetails = useMemo(() => {
        const lines = [];
        taxes.forEach((taxObj) => {
            if (taxObj.boat) {
                lines.push(_jsxs("div", { children: [taxObj.name, " - ", taxObj.rate, "%"] }, `tax-${taxObj.id}`));
            }
        });
        selectedGlobalAddons.forEach((addon) => {
            lines.push(_jsxs("div", { children: [addon.name, " - ", addon.percentage * 100, "% ", t('dateRangeSelector.min', { min: addon.minimum })] }, `addon-${addon.name}`));
        });
        lines.push(_jsxs("div", { children: [t('dateRangeSelector.platformFee'), " - ", (platformFee * 100).toFixed(2), "%"] }, "platform-fee-1"));
        lines.push(_jsxs("div", { children: [t('dateRangeSelector.transactionFee'), " - ", (interchangeFee * 100).toFixed(2), "%"] }, "platform-fee-2"));
        return _jsx(_Fragment, { children: lines });
    }, [taxes, selectedGlobalAddons, fee, t]);
    // When staff range is loading, display a Skeleton instead of a spinner.
    if (rangeLoading) {
        return (_jsxs(Box, { sx: { p: 2 }, children: [_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 300 }), _jsx(Skeleton, { variant: "text", sx: { mt: 2 } }), _jsx(Skeleton, { variant: "text" })] }));
    }
    // --- Render the Component ---
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h5", gutterBottom: true, sx: { fontWeight: 'bold', color: 'var(--primaryFgColor)!important' }, children: t('dateRangeSelector.selectDates') }), _jsxs(Box, { display: "flex", alignItems: "center", gap: 2, mb: 2, children: [_jsxs(Box, { display: "flex", alignItems: "center", gap: 0.5, children: [_jsx(Box, { width: 8, height: 8, bgcolor: "#28a745", borderRadius: "50%" }), _jsx(Typography, { variant: "caption", sx: { color: 'var(--primaryFgColor)!important' }, children: t('dateRangeSelector.legend.dailyHourly') })] }), _jsxs(Box, { display: "flex", alignItems: "center", gap: 0.5, children: [_jsx(Box, { width: 8, height: 8, bgcolor: "#007bff", borderRadius: "50%" }), _jsx(Typography, { variant: "caption", sx: { color: 'var(--primaryFgColor)!important' }, children: t('dateRangeSelector.legend.hourlyOnly') })] }), _jsxs(Box, { display: "flex", alignItems: "center", gap: 0.5, children: [_jsx(Box, { width: 8, height: 8, bgcolor: "purple", borderRadius: "50%" }), _jsx(Typography, { variant: "caption", sx: { color: 'var(--primaryFgColor)!important' }, children: t('dateRangeSelector.legend.waitlist') })] }), _jsxs(Box, { display: "flex", alignItems: "center", gap: 0.5, children: [_jsx(Box, { width: 8, height: 8, bgcolor: "grey", borderRadius: "50%" }), _jsx(Typography, { variant: "caption", sx: { color: 'var(--primaryFgColor)!important' }, children: t('dateRangeSelector.legend.unavailable') })] })] }), _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsxs(Box, { display: "flex", gap: 2, mt: 2, children: [_jsx(CustomDatePicker, { label: t('dateRangeSelector.start'), value: startDate, onChange: (date) => handleDateRangeChange([date, endDateVal]), shouldDisableDate: shouldDisableDate, getAvailabilityStatus: getAvailabilityStatus, onDayClick: (day) => handleWaitlistDayClick(day) }), (() => {
                            const status = startDate ? getAvailabilityStatus(startDate) : 'unavailable';
                            if (status !== 'hourlyOnly') {
                                return (_jsx(CustomDatePicker, { label: t('dateRangeSelector.end'), value: endDateVal, onChange: (date) => handleDateRangeChange([startDate, date]), shouldDisableDate: (day) => {
                                        const disabled = shouldDisableDate(day);
                                        const beforeStart = startDate ? day.isBefore(startDate, 'day') : false;
                                        return disabled || beforeStart;
                                    }, getAvailabilityStatus: getAvailabilityStatus, minDate: startDate || dayjs(), onDayClick: (day) => handleWaitlistDayClick(day) }));
                            }
                            return null;
                        })()] }) }), (isHourlyOnly || (useHourlyPricing && sameDaySelected)) && (_jsxs(Box, { mt: 3, children: [_jsx(FormLabel, { sx: { fontWeight: 'bold', color: 'var(--secondaryFgColor)!important' }, children: t('dateRangeSelector.selectDuration') }), _jsx(Select, { value: duration, onChange: (e) => setDuration(Number(e.target.value)), displayEmpty: true, sx: {
                            ml: 2,
                            color: 'var(--secondaryFgColor)!important',
                            border: '1px solid var(--secondaryFgColor)',
                            borderRadius: 1,
                            minWidth: 120,
                        }, children: allowedHours.map((h) => (_jsxs(MenuItem, { value: h, children: [h, " ", t('dateRangeSelector.hour', { count: h })] }, h))) }), _jsx(Box, { mt: 2, children: _jsx(FormLabel, { sx: { fontWeight: 'bold', color: 'var(--secondaryFgColor)!important' }, children: t('dateRangeSelector.availableTimes') }) }), _jsx(Box, { display: "flex", flexWrap: "wrap", gap: 1, mt: 1, children: timeSlots.length > 0 ? (timeSlots
                            .filter((slot) => !slot.disabled)
                            .map((slot, idx) => (_jsx(Chip, { label: slot.label, onClick: () => {
                                if (!startDate)
                                    return;
                                const selStart = startDate.hour(slot.start.hour()).minute(slot.start.minute());
                                const selEnd = selStart.add(duration, 'hour');
                                setSelectedSlot(slot.start);
                                handleTimeChange('start', selStart);
                                handleTimeChange('end', selEnd);
                            }, clickable: true, variant: "filled", sx: {
                                color: (selectedSlot === null || selectedSlot === void 0 ? void 0 : selectedSlot.isSame(slot.start))
                                    ? 'var(--primaryBgColor)!important'
                                    : 'var(--primaryFgColor)!important',
                                backgroundColor: (selectedSlot === null || selectedSlot === void 0 ? void 0 : selectedSlot.isSame(slot.start))
                                    ? 'var(--primaryFgColor)!important'
                                    : 'var(--primaryBgColor)!important',
                                border: '2px solid var(--primaryFgColor)!important',
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: 'var(--primaryFgColor)!important',
                                    color: 'var(--primaryBgColor)!important',
                                },
                            } }, idx)))) : (_jsx(Typography, { variant: "body2", color: "error", children: t('dateRangeSelector.noStaffAvailable') })) }), !selectedSlot && timeSlots.length > 0 && (_jsx(ErrorMessage, { message: t('dateRangeSelector.errorSelectTimeSlot') }))] })), dayRangeError && _jsx(ErrorMessage, { message: dayRangeError }), _jsx(ErrorMessage, { message: !isValidRange ? t('dateRangeSelector.errorInvalidRange') : null }), _jsx(Box, { mt: 3, children: (() => {
                    var _a, _b;
                    const flags = ((_b = (_a = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _a === void 0 ? void 0 : _a.details) === null || _b === void 0 ? void 0 : _b.flags) || {};
                    const globalAddons = [];
                    if (flags.AquaTow) {
                        globalAddons.push({
                            name: t('dateRangeSelector.addonTow.name', 'Tow'),
                            description: t('dateRangeSelector.addonTow.description', 'Tow coverage up to $500'),
                            percentage: 0.05,
                            minimum: 25,
                            icon: _jsx(DirectionsBoatIcon, { fontSize: "large" }),
                        });
                    }
                    if (flags.AquaShield) {
                        globalAddons.push({
                            name: t('dateRangeSelector.addonShield.name', 'Shield'),
                            description: t('dateRangeSelector.addonShield.description', 'Damage coverage up to $5000'),
                            percentage: 0.1,
                            minimum: 50,
                            icon: _jsx(ShieldIcon, { fontSize: "large" }),
                        });
                    }
                    if (flags.BlueBalance) {
                        globalAddons.push({
                            name: t('dateRangeSelector.addonBalance.name', 'Balance'),
                            description: t('dateRangeSelector.addonBalance.description', 'Offset your carbon footprint'),
                            percentage: 0.01,
                            minimum: 5,
                            icon: _jsx(LeafIcon, { color: "success", fontSize: "large" }),
                        });
                    }
                    if (!globalAddons.length)
                        return null;
                    return (_jsxs(_Fragment, { children: [_jsx(FormLabel, { sx: { fontWeight: 'bold', color: 'var(--secondaryFgColor)!important' }, children: t('dateRangeSelector.protectionPlans') }), _jsx(Box, { display: "flex", gap: 2, mt: 1, children: globalAddons.map((addon) => {
                                    const isSelected = selectedGlobalAddons.some((x) => x.name === addon.name);
                                    return (_jsx(Tooltip, { title: addon.description || '', arrow: true, children: _jsxs(ToggleButton, { value: addon.name, selected: isSelected, onChange: () => handleGlobalAddonChange(addon.name, addon.percentage, addon.minimum), sx: {
                                                borderRadius: '50%',
                                                width: 80,
                                                height: 80,
                                                border: isSelected ? '2px solid var(--actionColor)' : '1px solid var(--secondaryFgColor)',
                                                backgroundColor: isSelected ? 'var(--primaryBgColor)' : 'var(--background-color)',
                                                color: isSelected ? 'var(--primaryFgColor)' : 'var(--secondaryFgColor)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                                '&:hover': {
                                                    borderColor: 'var(--actionColor)',
                                                    backgroundColor: 'var(--primaryBgColor)',
                                                },
                                            }, children: [addon.icon, _jsx(Typography, { variant: "caption", sx: {
                                                        color: isSelected ? 'var(--primaryFgColor)!important' : 'var(--secondaryFgColor)!important',
                                                        fontWeight: isSelected ? 'bold' : 'normal',
                                                    }, children: addon.name })] }) }, addon.name));
                                }) })] }));
                })() }), ((_p = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Addons) === null || _p === void 0 ? void 0 : _p.length) > 0 && (_jsxs(Box, { mt: 3, children: [_jsx(FormLabel, { sx: { fontWeight: 'bold', color: 'var(--secondaryFgColor)!important', fontSize: '1.1rem' }, children: t('dateRangeSelector.additionalAddons') }), _jsx(Box, { display: "flex", flexWrap: "wrap", gap: 2, mt: 1, children: boatRefresh.Addons.map((addon) => {
                            const existing = selectedBoatAddons.find((x) => x.name === addon.name);
                            const qty = existing ? existing.quantity : 0;
                            let IconComponent = null;
                            if (addon.icon && FaIcons[addon.icon]) {
                                const FAIcon = FaIcons[addon.icon];
                                IconComponent = _jsx(FAIcon, { fontSize: "large" });
                            }
                            else if (addon.icon) {
                                const Fallback = FaIcons['FaQuestionCircle'];
                                IconComponent = _jsx(Fallback, { fontSize: "large" });
                            }
                            return (_jsx(Tooltip, { arrow: true, title: addon.description || t('dateRangeSelector.noDescriptionProvided'), children: _jsxs(Box, { sx: {
                                        position: 'relative',
                                        width: 140,
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: 'var(--background-color)',
                                        color: 'var(--secondaryFgColor)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: qty > 0 ? '0 0 10px rgba(0, 128, 0, 0.3)' : '0 2px 6px rgba(0,0,0,0.1)',
                                        '&:hover': { transform: 'scale(1.03)', boxShadow: '0 4px 10px rgba(0,0,0,0.15)' },
                                    }, children: [IconComponent && _jsx(Box, { mb: 1, sx: { color: 'var(--secondaryFgColor)' }, children: IconComponent }), _jsxs(Typography, { variant: "subtitle2", sx: { fontWeight: addon.required ? 'bold' : 500, color: 'var(--secondaryFgColor)!important', mb: 0.5 }, children: [addon.name, addon.required && (_jsxs(Typography, { component: "span", variant: "caption", color: "error", sx: { ml: 0.5 }, children: ["(", t('dateRangeSelector.required'), ")"] }))] }), _jsxs(Typography, { variant: "body2", sx: { color: 'var(--secondaryFgColor)!important', mb: 1 }, children: ["$", addon.price] }), _jsxs(Box, { display: "flex", alignItems: "center", gap: 1, children: [_jsx(IconButton, { size: "small", sx: { border: '1px solid var(--secondaryFgColor)', borderRadius: 1 }, onClick: () => handleDecrementBoatAddon(addon), children: _jsx(Remove, { fontSize: "small" }) }), _jsx(Typography, { variant: "body2", sx: { width: 24, textAlign: 'center', color: 'var(--secondaryFgColor)!important' }, children: qty }), _jsx(IconButton, { size: "small", sx: { border: '1px solid var(--secondaryFgColor)', borderRadius: 1 }, onClick: () => handleIncrementBoatAddon(addon), disabled: addon.maxQuantity ? qty >= addon.maxQuantity : false, children: _jsx(Add, { fontSize: "small" }) })] })] }) }, addon.name));
                        }) })] })), _jsx(GiftCardInput, { giftCardCode: giftCardCode, setGiftCardCode: setGiftCardCode, handleApplyGiftCard: handleApplyGiftCard, isApplyingPromo: isApplyingPromo, giftCardError: giftCardError }), staffAssignments.length > 0 && ((_r = (_q = boatRefresh === null || boatRefresh === void 0 ? void 0 : boatRefresh.Location) === null || _q === void 0 ? void 0 : _q.details) === null || _r === void 0 ? void 0 : _r.flags.ShowStaffAssignments) === true && (_jsxs(Box, { mt: 3, children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: t('dateRangeSelector.selectStaff') }), staffLoading && _jsx(Typography, { children: "Loading staff for each skill..." }), staffAssignments.map((assign) => {
                        const skill = assign.skill;
                        const availableStaff = availableStaffMap[skill] || [];
                        const selectedIds = selectedStaffMap[skill] || [];
                        // Compute set of staff IDs selected for all skills other than this one
                        const otherSelected = new Set(Object.entries(selectedStaffMap)
                            .filter(([k]) => k !== skill)
                            .flatMap(([, ids]) => ids));
                        // Filter available staff: if already selected for another skill, omit them
                        const filteredStaff = availableStaff.filter((s) => selectedIds.includes(s.id) ? true : !otherSelected.has(s.id));
                        return (_jsxs(Box, { mb: 2, children: [_jsx(Typography, { variant: "subtitle2", sx: { mb: 1, fontWeight: 'bold' }, children: skill.toUpperCase() }), _jsx(ComboboxBasic, { label: "", options: filteredStaff, value: filteredStaff.find((st) => selectedIds.includes(st.id)) || null, onChange: (_e, newVal) => {
                                        setSelectedStaffMap((prev) => (Object.assign(Object.assign({}, prev), { [skill]: newVal ? [newVal.id] : [] })));
                                    }, getOptionLabel: (option) => option.profileName || option.email || 'Staff' })] }, skill));
                    })] })), _jsx(Box, { mt: 3, children: _jsx(BookingBreakdown, { averagePrice: bookingDetailsState.averagePrice, daysBooked: bookingDetailsState.daysBooked, hoursBooked: bookingDetailsState.hoursBooked, subtotal: bookingDetailsState.subtotal, discount: bookingDetailsState.discount, giftCardDiscount: giftCardDiscount, discountType: discountType, fees: bookingDetailsState.fees, totalPrice: bookingDetailsState.totalPrice, loading: loading, addOnsTotal: bookingDetailsState.addonsTotal, tax: taxes, taxDetails: taxDetails, depositDueNow: bookingDetailsState.depositDueNow, depositPercentage: bookingDetailsState.depositPercentage, remainingBalance: bookingDetailsState.remainingBalance }) }), _jsx(Button, { fullWidth: true, variant: "contained", sx: {
                    mt: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    backgroundColor: 'var(--primaryFgColor)!important',
                    color: 'var(--primaryBgColor)!important',
                    '&:hover': { backgroundColor: 'var(--primaryFgColor)!important', opacity: 0.8 },
                    '&.Mui-disabled': { backgroundColor: 'grey!important', color: 'white!important' },
                }, onClick: handleBookNow, disabled: !startDate || !endDateVal || (isHourlyOnly && !selectedSlot) || Boolean(dayRangeError), children: t('dateRangeSelector.bookNow') }), _jsxs(Dialog, { open: isWaitlistOpen, onClose: () => setIsWaitlistOpen(false), children: [_jsx(DialogTitle, { children: t('dateRangeSelector.waitlistTitle') }), _jsx(DialogContent, { children: waitlistSuccess ? (_jsx(Typography, { color: "success.main", children: t('dateRangeSelector.waitlistSuccess') })) : (_jsxs(_Fragment, { children: [selectedWaitlistDate && (_jsx(Typography, { variant: "body2", mb: 2, children: t('dateRangeSelector.waitlistMessage', { date: selectedWaitlistDate.format('MMMM D, YYYY') }) })), _jsx(TextField, { label: t('dateRangeSelector.firstName'), name: "firstName", value: waitlistForm.firstName, onChange: handleWaitlistInputChange, fullWidth: true, margin: "dense" }), _jsx(TextField, { label: t('dateRangeSelector.lastName'), name: "lastName", value: waitlistForm.lastName, onChange: handleWaitlistInputChange, fullWidth: true, margin: "dense" }), _jsx(TextField, { label: t('dateRangeSelector.emailAddress'), name: "email", value: waitlistForm.email, onChange: handleWaitlistInputChange, fullWidth: true, margin: "dense" }), waitlistError && _jsx(Typography, { variant: "body2", color: "error", mt: 1, children: waitlistError })] })) }), _jsx(DialogActions, { children: !waitlistSuccess ? (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setIsWaitlistOpen(false), children: t('dateRangeSelector.cancel') }), _jsx(Button, { onClick: handleWaitlistSubmit, disabled: waitlistSubmitting, children: waitlistSubmitting ? _jsx(CircularProgress, { size: 24 }) : t('dateRangeSelector.submit') })] })) : (_jsx(Button, { onClick: () => setIsWaitlistOpen(false), children: t('dateRangeSelector.close') })) })] }), _jsx(Modal, { open: checkoutOpen, onClose: () => setCheckoutOpen(false), children: _jsx(Box, { sx: {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: isSmallScreen ? '100vw' : '90vw',
                        maxWidth: 1900,
                        height: isSmallScreen ? '100vh' : '90vh',
                        maxHeight: 900,
                        bgcolor: 'var(--primaryBgColor)',
                        boxShadow: 24,
                        p: 4,
                        overflowY: 'auto',
                        borderRadius: 2,
                    }, children: _jsx(IntegratedCheckout, { state: checkoutData }) }) })] }));
}
